import type { Intent } from '../types/badges'
import type { SelectFilterOption } from '../types/filter'

export const UpdateStatuses = {
    InProgress: 0,
    RequestFinished: 1,
    Success: 2,
    Failure: 3,
    Created: 4,
    Downloaded: 5,
    Ingested: 6,
} as const

type UpdateStatusMeta = {
    statusId: number
    label: string
    filterLabel: string
    intent?: Intent
    description: string
}

// Keep this ordered by status ID: these values are persisted by the ingestion pipeline.
export const updateStatusMetadata: readonly UpdateStatusMeta[] = [
    {
        statusId: UpdateStatuses.InProgress,
        label: 'In Progress',
        filterLabel: 'In Progress',
        description: 'Requested from Amazon. Waiting for Amazon to generate it.',
    },
    {
        statusId: UpdateStatuses.RequestFinished,
        label: 'Request Finished',
        filterLabel: 'Request Finished',
        intent: 'primary',
        description: 'Amazon finished generating it. Ready to download.',
    },
    {
        statusId: UpdateStatuses.Success,
        label: 'Success',
        filterLabel: 'Success',
        intent: 'success',
        description: 'Complete. Rows ingested and confirmed loaded downstream.',
    },
    {
        statusId: UpdateStatuses.Failure,
        label: 'Failure',
        filterLabel: 'Failed',
        intent: 'danger',
        description:
            'Terminal failure. Load retries were exhausted, or Amazon failed to generate it.',
    },
    {
        statusId: UpdateStatuses.Created,
        label: 'Created',
        filterLabel: 'Created',
        intent: 'primary',
        description: 'Queued. Not requested from Amazon yet.',
    },
    {
        statusId: UpdateStatuses.Downloaded,
        label: 'Downloaded',
        filterLabel: 'Downloaded',
        intent: 'primary',
        description: 'File stored in S3. Waiting for ingestion.',
    },
    {
        statusId: UpdateStatuses.Ingested,
        label: 'Ingested',
        filterLabel: 'Ingested',
        intent: 'primary',
        description:
            'Rows ingested into Postgres. Waiting for load confirmation; retries with backoff.',
    },
]

export const updateStatusMetaById: Readonly<Record<number, UpdateStatusMeta>> = Object.fromEntries(
    updateStatusMetadata.map((metadata) => [metadata.statusId, metadata]),
)

export const updateStatusBadgeOptions = Object.fromEntries(
    updateStatusMetadata.map((metadata) => [
        metadata.statusId,
        { text: metadata.label, intent: metadata.intent },
    ]),
)

export const updateStatusFilterOptions: SelectFilterOption[] = updateStatusMetadata.map(
    ({ statusId, filterLabel }) => ({ value: statusId, name: filterLabel }),
)

export const updateStatusString = (statusId: number): string => {
    const metadata = updateStatusMetaById[statusId]
    if (!metadata) {
        console.error('Cannot resolve status string for', statusId)
        return String(statusId)
    }

    return metadata.label
}
